import { Button, HStack, Text } from "@chakra-ui/react";
import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import { MessageFragment, MessageThreadFragment } from "gql/graphql";
import { CreateTaskAiIcon } from "modules/icons";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { useOverlay } from "modules/providers/overlay-provider";
import { DRAWER_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { TASK_DRAFT_ID } from "modules/utils/Constants";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import SyncoTooltip from "modules/_shared/synco-tooltip";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  displayText?: string;
}

const MessagePopoverGenerateTaskButton: React.FC<IProps> = ({
  messageThread,
  message,
  displayText,
}) => {
  const { openDrawer } = useOverlay();
  const { createDraft, setQuotedMessages, setRelatedMessageThread } =
    useMessageThreadDraft({
      draftId: TASK_DRAFT_ID,
    });

  const onClick = useCallback(
    async (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      await createDraft({
        type: messageThread.type,
        draftType: "new-thread",
      });
      await setQuotedMessages(message);
      await setRelatedMessageThread(messageThread);
      openDrawer({
        type: DRAWER_TYPE.GENERATE_TASK_FROM_MESSAGES,
        id: messageThread.id,
      });
    },
    [
      createDraft,
      message,
      messageThread,
      openDrawer,
      setQuotedMessages,
      setRelatedMessageThread,
    ],
  );

  const isVisible = useMemo(
    () => !messageThread.isLocked,
    [messageThread.isLocked],
  );

  if (!isVisible) {
    return null;
  }

  if (displayText) {
    return (
      <Button variant="link" onClick={onClick}>
        <HStack spacing={2}>
          <CreateTaskAiIcon boxSize={5} fill={colors.blue} />
          <Text fontSize={14} color={colors.blackSecondary}>
            {displayText}
          </Text>
        </HStack>
      </Button>
    );
  }

  return (
    <SyncoTooltip
      label="Create Task from Message"
      wrapInBox={false}
      openDelay={1000}
      placement="top-end"
    >
      <CircularIconButton
        size="xs"
        variant="link"
        icon={<CreateTaskAiIcon boxSize={5} fill={colors.blue} />}
        onClick={onClick}
        aria-label="create task from message"
      />
    </SyncoTooltip>
  );
};

export default memo(MessagePopoverGenerateTaskButton);
